import { Filter } from '@cubejs-client/core';
import { useCubeQuery } from '@cubejs-client/react';
import { DescriptionOutlined } from '@mui/icons-material';
import {
  LinearProgressList,
  Widget,
  WidgetContent,
  WidgetHeader,
  WidgetIcon,
  useScrollIntoView,
} from '@procureai/ui-mui';
import { useTranslation } from 'react-i18next';

import { I18nNamespace, NegotiationStatus } from '~/common/types';
import { DimNegotiationCaseDimension, DimNegotiationCaseMeasure } from '~/common/types/cubes/DimNegotiationCase';
import { SpendType } from '~/common/types/cubes/SpendType';
import { useDateRange } from '~/components/DateRangeProvider';
import { useResponsibleUsersFilter } from '~/components/ResponsibleUsersProvider';
import { useSpendTypeFilter } from '~/hooks';
import { convertDateRangeToTimeDimension } from '~/util/cube';
import { toNumber } from '~/util/cube/toNumber';

import { NegotiationCaseCountListitem } from './components/NegotiationCaseCountListitem';
import { statusFilterMap } from './NegotiationCountWidget.util';

export type NegotiationCountWidgetProps = {
  filters?: Filter[];
  spendType: SpendType;
};
export type UseNegotiationCaseCountByStatusProps = { variant: NegotiationStatus } & NegotiationCountWidgetProps;

const useNegotiationCaseCountByStatus = ({
  variant,
  filters = [],
  spendType,
}: UseNegotiationCaseCountByStatusProps) => {
  const { currentDateRange } = useDateRange();
  const responsibleUsersFilter = useResponsibleUsersFilter(DimNegotiationCaseDimension.RESPONSIBLE_USER_ID);
  const statusFilter = statusFilterMap[variant];

  const spendTypeFilter = useSpendTypeFilter({
    spendType,
    cubeTotalVolumeField: DimNegotiationCaseDimension.TOTAL_VALUE,
  });
  const result = useCubeQuery({
    measures: [DimNegotiationCaseMeasure.COUNT],
    timeDimensions: convertDateRangeToTimeDimension(DimNegotiationCaseDimension.CREATED_AT, currentDateRange),
    filters: [...filters, ...statusFilter, responsibleUsersFilter, ...spendTypeFilter],
  });

  const value = result?.resultSet?.rawData()?.[0]?.[DimNegotiationCaseMeasure.COUNT];
  return {
    result,
    value: toNumber(value) ?? 0,
  };
};

export const NegotiationCountWidget = ({ filters, spendType }: NegotiationCountWidgetProps) => {
  const { t } = useTranslation(I18nNamespace.base, { keyPrefix: 'NegotiationStatisticsWidget' });
  const [widgetRef, active] = useScrollIntoView({ hash: 'negotiation-process' });
  const { value: allNegotiationCases } = useNegotiationCaseCountByStatus({
    variant: NegotiationStatus.ALL,
    spendType,
    filters,
  });
  const { value: runningNegotiationCases } = useNegotiationCaseCountByStatus({
    variant: NegotiationStatus.RUNNING,
    spendType,
    filters,
  });
  const { value: successfulNegotiationCases } = useNegotiationCaseCountByStatus({
    variant: NegotiationStatus.SUCCESSFUL,
    spendType,
    filters,
  });
  const { value: failedNegotiationCases } = useNegotiationCaseCountByStatus({
    variant: NegotiationStatus.FAILED,
    spendType,
    filters,
  });
  const { value: cancelledNegotiationCases } = useNegotiationCaseCountByStatus({
    variant: NegotiationStatus.CANCELLED,
    spendType,
    filters,
  });
  const { value: cancelledByUserNegotiationCases } = useNegotiationCaseCountByStatus({
    variant: NegotiationStatus.CANCELLED_BY_USER,
    spendType,
    filters,
  });
  const { value: cancelledBySystemNegotiationCases } = useNegotiationCaseCountByStatus({
    variant: NegotiationStatus.CANCELLED_BY_SYSTEM,
    spendType,
    filters,
  });

  return (
    <Widget ref={widgetRef} active={active}>
      <WidgetHeader
        title={t(`title.${spendType}`)}
        icon={<WidgetIcon icon={<DescriptionOutlined fontSize="small" />} />}
        subTitle={t(`subTitle.${spendType}`)}
      />
      <WidgetContent>
        <LinearProgressList>
          <NegotiationCaseCountListitem
            variant={NegotiationStatus.ALL}
            min={0}
            value={allNegotiationCases}
            max={allNegotiationCases}
          />
          <NegotiationCaseCountListitem
            variant={NegotiationStatus.RUNNING}
            min={0}
            value={runningNegotiationCases}
            max={allNegotiationCases}
          />
          <NegotiationCaseCountListitem
            variant={NegotiationStatus.SUCCESSFUL}
            min={0}
            value={successfulNegotiationCases}
            max={allNegotiationCases}
          />
          <NegotiationCaseCountListitem
            variant={NegotiationStatus.CANCELLED}
            min={0}
            value={cancelledNegotiationCases}
            max={allNegotiationCases}
          />
          <NegotiationCaseCountListitem
            variant={NegotiationStatus.CANCELLED_BY_USER}
            min={0}
            value={cancelledByUserNegotiationCases}
            max={allNegotiationCases}
          />
          <NegotiationCaseCountListitem
            variant={NegotiationStatus.CANCELLED_BY_SYSTEM}
            min={0}
            value={cancelledBySystemNegotiationCases}
            max={allNegotiationCases}
          />
          <NegotiationCaseCountListitem
            variant={NegotiationStatus.FAILED}
            min={0}
            value={failedNegotiationCases}
            max={allNegotiationCases}
          />
        </LinearProgressList>
      </WidgetContent>
    </Widget>
  );
};
